import React from 'react';
import { DropdownButton, Dropdown } from 'react-bootstrap';
import './CustomDropdown.css';

export interface CustomDropdownOption<K extends string = string> {
    key: K;
    name: string;
}

export interface CustomDropdownProps<K extends string = string> {
    /** Header label above the control. Omit (or pass empty) to render no header. */
    label?: string;
    options: CustomDropdownOption<K>[];
    selectedKey: K;
    onSelect: (key: K) => void;
    disabled?: boolean;
    /** Optional icon class (e.g. "pi pi-sort-alt") shown before the title. */
    icon?: string;
    /**
     * When true (default), a leading "Alla" item is shown and an empty
     * `selectedKey` means "all". Set false for controls that always carry an
     * active selection (e.g. sort), which then show the selected option's name
     * as the title and render no "Alla" item.
     */
    showAllOption?: boolean;
    /**
     * When true, renders a compact button with no dropdown caret, sized for
     * a short label instead of react-bootstrap's default width.
     */
    compact?: boolean;
    /**
     * Overrides the displayed title text (e.g. a short abbreviation) while
     * leaving the selected key and menu options unchanged.
     */
    titleOverride?: string;
}

/**
 * CustomDropdown wraps React-Bootstrap's DropdownButton/Dropdown in order to
 * provide a consistent, stylable single-select control. With `showAllOption`
 * it doubles as a filter ("Alla" = no filter) or a plain selector.
 */
function CustomDropdown<K extends string = string>({
    label = '',
    options,
    selectedKey,
    onSelect,
    disabled = false,
    showAllOption = true,
    icon,
    compact = false,
    titleOverride,
}: CustomDropdownProps<K>) {
    const selectedName = options.find((opt) => opt.key === selectedKey)?.name;
    // Empty selectedKey means "Alla" only for the filter variant. A truthy but
    // unknown key shows a blank title (matching the pre-generic behavior) rather
    // than falling back to "Alla".
    const title = selectedKey ? (selectedName ?? '') : (showAllOption ? 'Alla' : '');
    const displayTitle = titleOverride ?? title;
    const idSlug = (label || 'dropdown').toLowerCase().replace(/\s+/g, '-');
    const titleContent = icon ? (
        <>
            <i className={icon} style={displayTitle ? { marginRight: '8px' } : undefined} />
            {displayTitle}
        </>
    ) : (
        displayTitle
    );

    return (
        <div className="custom-dropdown-root">
            {label && <label className="custom-dropdown-label">{label}</label>}

            <DropdownButton
                id={`${idSlug}-dropdown`}
                variant="secondary"
                title={titleContent}
                disabled={disabled}
                className={`custom-dropdown-button${compact ? ' custom-dropdown-button--compact' : ''}`}
            >
                {showAllOption && (
                    <Dropdown.Item
                        active={!selectedKey}
                        onClick={() => onSelect('' as K)}
                        className="custom-dropdown-item"
                    >
                        Alla
                    </Dropdown.Item>
                )}

                {options.map((option) => (
                    <Dropdown.Item
                        key={option.key}
                        active={option.key === selectedKey}
                        onClick={() => onSelect(option.key)}
                        className="custom-dropdown-item"
                    >
                        {option.name}
                    </Dropdown.Item>
                ))}
            </DropdownButton>
        </div>
    );
}

export default CustomDropdown;
